import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  IconButton,
  Popper,
  ClickAwayListener,
  useTheme,
  SxProps,
} from "@mui/material";
import { MoreHorizontal } from "lucide-react";

interface RatingActionItem {
  name: string;
  jsx: React.ReactNode;
}

interface RatingDropdownProps {
  actions: RatingActionItem[];
  onActionClick?: (closeHandler: () => void) => void;
  popperSx?: SxProps;
}

const RatingDropdown: React.FC<RatingDropdownProps> = ({
  actions,
  onActionClick,
  popperSx,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  const open = Boolean(anchorEl);

  const handleToggle = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Expose handleClose to parent via onActionClick callback
  React.useEffect(() => {
    if (onActionClick) {
      onActionClick(handleClose);
    }
  }, [handleClose, onActionClick]);

  const handleClickAway = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (
        contentRef.current?.contains(event.target as Node) ||
        anchorEl?.contains(event.target as Node)
      ) {
        return;
      }
      handleClose();
    },
    [anchorEl, handleClose]
  );

  if (actions.length === 0) {
    return null;
  }

  return (
    <Box sx={{ position: "relative" }}>
      <IconButton
        onClick={handleToggle}
        sx={{
          color: theme.nawy.colors.semantic.text.primary,
          padding: theme.nawy.spacing.xs,
          "&:hover": {
            backgroundColor: theme.nawy.colors.semantic.layer.secondary,
          },
        }}
      >
        <MoreHorizontal size={16} />
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="bottom-end"
        sx={{
          zIndex: 1500, // Higher than Drawer (1200)
          ...popperSx,
        }}
        modifiers={[
          { name: "flip", enabled: true },
          {
            name: "preventOverflow",
            enabled: true,
            options: { altAxis: true, rootBoundary: "viewport" },
          },
        ]}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box
            ref={contentRef}
            sx={{
              width: "fit-content",
              minWidth: "150px",
              border: "1px solid #D0D5DD",
              backgroundColor: "white",
              borderRadius: theme.nawy.radius.lg,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.10)",
              marginTop: theme.nawy.spacing.xs,
              padding: theme.nawy.spacing.xs,
            }}
          >
            {actions.map((item, index) => (
              <Box key={index}>{item.jsx}</Box>
            ))}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default RatingDropdown;
